import { useState, useEffect, useRef } from "react";
import { MonitorPlay, Clock, User, ArrowRight, Volume2 } from "lucide-react";
import { io } from "socket.io-client";

interface Appointment {
  id: string;
  patientId: string;
  dateTime: string;
  status: "SCHEDULED" | "IN_PROGRESS" | "COMPLETED" | "CANCELLED";
  queueNumber?: number;
}

interface Patient {
  id: string;
  name: string;
}

export default function QueueDisplay() {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [patients, setPatients] = useState<Patient[]>([]);
  const [lastAnnouncedId, setLastAnnouncedId] = useState<string | null>(null);
  const [isAudioEnabled, setIsAudioEnabled] = useState(false);

  const fetchQueue = async () => {
    try {
      const apiToken = localStorage.getItem("clinic_token");
      if (!apiToken) return;

      const pRes = await fetch("/api/patients", { headers: { Authorization: `Bearer ${apiToken}` } });
      if (pRes.ok) setPatients(await pRes.json());

      const aRes = await fetch("/api/appointments", { headers: { Authorization: `Bearer ${apiToken}` } });
      if (aRes.ok) setAppointments(await aRes.json());
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    fetchQueue();
    const interval = setInterval(fetchQueue, 5000); // Fallback polling
    
    // Listen to real-time socket events
    const socket = io();
    socket.on("db-updated", () => {
      fetchQueue();
    });

    return () => {
      clearInterval(interval);
      socket.disconnect();
    };
  }, []);

  // Announce newly active patients
  useEffect(() => {
    if (!isAudioEnabled) return;

    // Sort today's appointments
    const todayStr = new Date().toISOString().split("T")[0];
    const todayActive = appointments.filter(a => a.dateTime.startsWith(todayStr) && a.status === "IN_PROGRESS");
    
    if (todayActive.length > 0) {
      const current = todayActive[0];
      if (current.id !== lastAnnouncedId) {
        setLastAnnouncedId(current.id);
        const patientName = patients.find(p => p.id === current.patientId)?.name || "المريض";
        const queueNum = current.queueNumber || "";
        
        const textToSpeak = `الرقم ${queueNum}، المراجع ${patientName}، تفضل إلى غرفة الطبيب`;
        const utterance = new SpeechSynthesisUtterance(textToSpeak);
        utterance.lang = "ar-SA";
        utterance.rate = 0.85;
        window.speechSynthesis.speak(utterance);
      }
    }
  }, [appointments, patients, lastAnnouncedId, isAudioEnabled]);

  const todayStr = new Date().toISOString().split("T")[0];
  const todayQueue = appointments
    .filter(a => a.dateTime.startsWith(todayStr) && (a.status === "SCHEDULED" || a.status === "IN_PROGRESS"))
    .sort((a, b) => new Date(a.dateTime).getTime() - new Date(b.dateTime).getTime());

  const inProgress = todayQueue.filter(a => a.status === "IN_PROGRESS");
  const waiting = todayQueue.filter(a => a.status === "SCHEDULED");

  const getPatientName = (id: string) => patients.find(p => p.id === id)?.name || "Unknown Patient";

  return (
    <div className="min-h-screen bg-slate-900 text-white font-sans flex flex-col" dir="rtl">
      {/* Header */}
      <header className="bg-slate-800 border-b border-slate-700 p-6 flex items-center justify-between shadow-lg">
        <div className="flex items-center gap-4">
          <MonitorPlay className="w-10 h-10 text-emerald-400" />
          <div>
            <h1 className="text-3xl font-black text-white tracking-wider">الدكتور واثق الجلبي</h1>
            <p className="text-slate-400 text-sm font-medium tracking-widest mt-1">شاشة الانتظار الرئيسية - IQ-DOCTOR QUEUE SYSTEM</p>
          </div>
        </div>
        <div className="flex items-center gap-4">
          <button 
            onClick={() => setIsAudioEnabled(!isAudioEnabled)}
            className={`flex items-center gap-2 px-4 py-2 rounded-full font-bold transition ${isAudioEnabled ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30" : "bg-rose-500/20 text-rose-400 border border-rose-500/30"}`}
          >
            <Volume2 className="w-5 h-5" />
            {isAudioEnabled ? "النداء الآلي مفعل" : "النداء الآلي معطل (انقر للتفعيل)"}
          </button>
          <div className="text-right flex items-center gap-3 bg-slate-950 px-5 py-2.5 rounded-xl border border-slate-700">
            <Clock className="w-6 h-6 text-indigo-400" />
            <span className="text-2xl font-mono font-bold tracking-widest">{new Date().toLocaleTimeString("ar-IQ", { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 p-8 flex gap-8">
        {/* Left/Right Column: Current Patient */}
        <div className="w-1/2 flex flex-col gap-6">
          <div className="bg-emerald-950/30 border border-emerald-500/30 rounded-3xl p-8 flex-1 flex flex-col items-center justify-center text-center relative overflow-hidden shadow-2xl">
            <div className="absolute top-0 w-full h-2 bg-emerald-500 animate-pulse"></div>
            <h2 className="text-4xl font-bold text-emerald-400 mb-8 uppercase tracking-widest">تفضل بالدخول الآن</h2>
            
            {inProgress.length > 0 ? (
              <div className="space-y-6 animate-in slide-in-from-bottom-8 duration-700">
                {inProgress[0].queueNumber && (
                  <div className="text-8xl font-black text-white bg-emerald-600 w-48 h-48 mx-auto rounded-full flex items-center justify-center shadow-[0_0_60px_rgba(16,185,129,0.4)]">
                    {inProgress[0].queueNumber}
                  </div>
                )}
                <div className="text-5xl font-black text-white mt-8">{getPatientName(inProgress[0].patientId)}</div>
                <div className="text-2xl text-emerald-300 font-bold flex items-center justify-center gap-3 mt-6 bg-emerald-900/40 py-3 px-8 rounded-full border border-emerald-500/20 inline-flex">
                  <ArrowRight className="w-8 h-8" />
                  غرفة الطبيب
                </div>
              </div>
            ) : (
              <div className="text-3xl text-slate-500 font-medium opacity-50 flex flex-col items-center gap-4">
                <User className="w-24 h-24 mb-4" />
                لا يوجد مريض حالياً في الداخل
              </div>
            )}
          </div>
        </div>

        {/* Next in Queue */}
        <div className="w-1/2 flex flex-col gap-6">
          <div className="bg-slate-800 border border-slate-700 rounded-3xl p-8 flex-1 shadow-2xl">
            <h2 className="text-3xl font-bold text-indigo-400 mb-8 border-b border-slate-700 pb-6 flex items-center gap-4">
              <User className="w-8 h-8" />
              قائمة الانتظار القادمة
            </h2>
            
            <div className="space-y-4">
              {waiting.length > 0 ? (
                waiting.slice(0, 6).map((apt, index) => (
                  <div key={apt.id} className="bg-slate-900 border border-slate-700 p-5 rounded-2xl flex items-center justify-between transform transition duration-300 hover:scale-[1.02]">
                    <div className="flex items-center gap-6">
                      <div className="bg-indigo-900/50 text-indigo-300 w-16 h-16 rounded-xl flex items-center justify-center text-3xl font-black border border-indigo-500/30 shadow-inner">
                        {apt.queueNumber || (index + 1)}
                      </div>
                      <span className="text-3xl font-bold text-slate-200">{getPatientName(apt.patientId)}</span>
                    </div>
                    <div className="text-slate-400 font-mono text-2xl bg-slate-950 px-4 py-2 rounded-lg border border-slate-800 shadow-inner">
                      {new Date(apt.dateTime).toLocaleTimeString("ar-IQ", { hour: '2-digit', minute: '2-digit' })}
                    </div>
                  </div>
                ))
              ) : (
                <div className="text-center text-slate-500 py-12 text-2xl font-medium">
                  لا يوجد مراجعين في قائمة الانتظار
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
